import React, { useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Download, Upload, Loader2, AlertTriangle, CheckCircle2, Calculator } from 'lucide-react';

const BulkSalaryRevision = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/payroll/bulk-revision/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'salary_revision_template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) { toast.error('Download failed'); }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
  };

  const dryRun = async () => {
    if (!file) { toast.error('Select an Excel file first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/payroll/bulk-revision/upload?dry_run=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      toast.success(`Parsed: ${res.data.matched.length} matched, ${res.data.unmatched.length} unmatched`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Parse failed');
    } finally { setUploading(false); }
  };

  const apply = async () => {
    if (!file || !preview) return;
    if (!window.confirm(`Apply salary revisions to ${preview.matched.length} employees? This cannot be undone.`)) return;
    setApplying(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/payroll/bulk-revision/upload?dry_run=false', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Applied to ${res.data.applied_count} employees`);
      setPreview(res.data);
      setFile(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };

  return (
    <div className="space-y-6" data-testid="bulk-salary-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Calculator />Bulk Salary Revision</h1>
        <p className="text-muted-foreground">Upload an Excel sheet to apply salary increments to multiple employees at once</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Step 1: Download Template</CardTitle>
          <CardDescription>Template is pre-filled with all active employees and their current basic salary. Fill the "New Basic" column for employees you want to revise.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={downloadTemplate} variant="outline" data-testid="download-template-btn">
            <Download size={16} className="mr-2" />Download Template
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Step 2: Upload Filled Template</CardTitle>
          <CardDescription>Upload your edited Excel file. A dry-run preview will show what will change.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 items-center">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFile}
              className="flex-1 text-sm"
              data-testid="bulk-file-input"
            />
            <Button onClick={dryRun} disabled={!file || uploading} data-testid="preview-btn">
              {uploading && <Loader2 size={14} className="animate-spin mr-1" />}
              <Upload size={14} className="mr-2" />Preview
            </Button>
          </div>
          {file && <p className="text-xs text-muted-foreground">Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
        </CardContent>
      </Card>

      {preview && (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Step 3: Review & Apply</CardTitle>
                  <CardDescription>
                    {preview.dry_run ? 'Preview mode — no changes saved yet' : 'Applied successfully'}
                  </CardDescription>
                </div>
                {preview.dry_run && preview.matched.length > 0 && (
                  <Button onClick={apply} disabled={applying} data-testid="apply-revision-btn">
                    {applying && <Loader2 size={14} className="animate-spin mr-1" />}
                    <CheckCircle2 size={16} className="mr-2" />Apply to {preview.matched.length} Employees
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
                <div className="p-3 bg-emerald-500/10 rounded">
                  <p className="text-xs text-muted-foreground">Matched</p>
                  <p className="text-2xl font-bold text-emerald-600">{preview.matched.length}</p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded">
                  <p className="text-xs text-muted-foreground">Unmatched</p>
                  <p className="text-2xl font-bold text-amber-600">{preview.unmatched.length}</p>
                </div>
                <div className="p-3 bg-muted/30 rounded">
                  <p className="text-xs text-muted-foreground">Skipped (empty/invalid)</p>
                  <p className="text-2xl font-bold">{preview.skipped.length}</p>
                </div>
                <div className="p-3 bg-primary/10 rounded">
                  <p className="text-xs text-muted-foreground">{preview.dry_run ? 'Will Apply' : 'Applied'}</p>
                  <p className="text-2xl font-bold text-primary">{preview.dry_run ? preview.matched.length : preview.applied_count}</p>
                </div>
              </div>

              {preview.unmatched.length > 0 && (
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/40 rounded text-sm">
                  <p className="font-semibold flex items-center gap-2"><AlertTriangle size={14} />Unmatched Employee Codes:</p>
                  <p className="text-xs mt-1">{preview.unmatched.map(u => u.code).join(', ')}</p>
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Old Basic</TableHead>
                    <TableHead>New Basic</TableHead>
                    <TableHead>Δ</TableHead>
                    <TableHead>%</TableHead>
                    <TableHead>Effective</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.matched.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No matches yet</TableCell></TableRow>}
                  {preview.matched.map((r, i) => (
                    <TableRow key={i} data-testid={`revision-row-${r.code}`}>
                      <TableCell className="font-mono">{r.code}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.old_basic?.toLocaleString()}</TableCell>
                      <TableCell className="font-bold">{r.new_basic?.toLocaleString()}</TableCell>
                      <TableCell className={r.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                        {r.delta >= 0 ? '+' : ''}{r.delta?.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={r.delta_pct >= 0 ? 'default' : 'destructive'}>
                          {r.delta_pct >= 0 ? '+' : ''}{r.delta_pct}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{r.effective_date || '—'}</TableCell>
                      <TableCell className="text-xs max-w-xs truncate">{r.notes || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default BulkSalaryRevision;
